import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import type { CreateCustomLabelInput, CreateCustomLabelMutation } from '@core/graphql/types';
import { track } from '@core/logging';
import { CREATE_CUSTOM_LABEL, GET_CUSTOM_LABELS } from '@moderation/graphql';
import { useState, useCallback } from 'react';

/**
 * Hook to create a custom label
 * @param communityId The ID of the community
 * @returns Mutation function, loading state, errors, and utilities
 */
export function useCreateCustomLabel(communityId: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [createCustomLabelMutation, { loading }] = useMutation<CreateCustomLabelMutation>(CREATE_CUSTOM_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createCustomLabel = async (input: CreateCustomLabelInput): Promise<boolean> => {
    try {
      track('CreateCustomLabelInitiated', {
        communityId,
        labelName: input.name,
      });

      await createCustomLabelMutation({
        variables: { communityId, input },
        // Cache sync: refetch custom labels after creation to ensure list consistency
        refetchQueries: [{ query: GET_CUSTOM_LABELS, variables: { communityId } }],
        awaitRefetchQueries: true,
      });

      track('CreateCustomLabelSucceeded', {
        communityId,
        labelName: input.name,
      });

      return true;
    } catch (error) {
      track('CreateCustomLabelFailed', {
        communityId,
        labelName: input.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    createCustomLabel,
    loading,
    errors,
    resetErrors,
  };
}
